import type { QueryClient, QueryKey } from '@tanstack/react-query';

/*
 * The server keeps no cache of the panel (reports/routes.ts): a figure is stale only in this browser. Every mutation
 * that can change a financial figure (import commit, workflow transition, deposit, expense, allocation, exception)
 * calls invalidateFinancialViews, so the panel and the reports are fetched again the next time they are shown, and
 * at once when they are on screen (docs/data/IMPORT_ARCHITECTURE.md §2 Dashboard; defect R8 of the baseline).
 */

/** Query keys (prefixes) of the panel: its three blocks. */
export const DASHBOARD_KEYS: readonly QueryKey[] = [
  ['dashboard'],
  ['dashboard-month'],
  ['dashboard-channels'],
];

/** Every view whose figures come from closed, checked or approved days, deposits, expenses or reconciliation. */
export const FINANCIAL_VIEW_KEYS: readonly QueryKey[] = [
  ...DASHBOARD_KEYS,
  ['daily'],
  ['weekly'],
  ['monthly'],
  ['comparatives'],
  ['revenue'],
  ['dre'],
  ['report-reconciliation'],
  ['report-exceptions'],
  ['movement'],
  ['approvals'],
  ['import-batches'],
  ['import-batch'],
  ['import-rows'],
];

/** Reconciliation screen blocks; only marked stale from outside that screen (it refetches its own after a change). */
export const RECONCILIATION_KEYS: readonly QueryKey[] = [
  ['recon-overview'],
  ['recon-queue'],
  ['recon-rows'],
  ['recon-rec'],
  ['recon-dep'],
  ['recon-allocations'],
];

const CHANNEL = 'ana-financial-views';

export interface InvalidateOptions {
  /** Also the reconciliation blocks (a statement import changes what is left to reconcile). */
  reconciliation?: boolean;
  /** Tell the other tabs of this browser (default). A tab reacting to another tab's message does not echo it. */
  broadcast?: boolean;
}

/**
 * Marks the financial views stale and refetches the ones on screen. Also tells the other open tabs, so a panel left
 * open in another tab updates without a reload.
 */
export async function invalidateFinancialViews(qc: QueryClient, opts: InvalidateOptions = {}): Promise<void> {
  const keys = opts.reconciliation ? [...FINANCIAL_VIEW_KEYS, ...RECONCILIATION_KEYS] : FINANCIAL_VIEW_KEYS;
  if (opts.broadcast !== false) announce(Boolean(opts.reconciliation));
  await Promise.all(keys.map((queryKey) => qc.invalidateQueries({ queryKey })));
}

function announce(reconciliation: boolean): void {
  if (typeof BroadcastChannel !== 'function') return;
  try {
    const ch = new BroadcastChannel(CHANNEL);
    ch.postMessage({ type: 'financial-views-changed', reconciliation });
    ch.close();
  } catch {
    /* channel unavailable (old browser, sandboxed frame): the other tabs refresh on their own interval */
  }
}

/**
 * Listens for changes made in other tabs of this browser. Returns the cleanup. Without BroadcastChannel the other
 * tabs still refresh through refetchInterval and on window focus.
 */
export function listenFinancialViews(qc: QueryClient): () => void {
  if (typeof BroadcastChannel !== 'function') return () => {};
  let ch: BroadcastChannel;
  try {
    ch = new BroadcastChannel(CHANNEL);
  } catch {
    return () => {};
  }
  ch.onmessage = (e: MessageEvent<{ type?: string; reconciliation?: boolean }>) => {
    if (e.data?.type !== 'financial-views-changed') return;
    void invalidateFinancialViews(qc, { broadcast: false, reconciliation: Boolean(e.data.reconciliation) });
  };
  return () => ch.close();
}
